import {
    Box,
    Button,
    FormField,
    Input,
    Modal,
    Select,
    SelectProps,
    SpaceBetween,
    TextContent
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppError } from "@amzn/ask-legal-domain";
import { LinkableNode } from "../../../utils/decisiontree/linkable-nodes-filter";
import { LoadingSpinner } from "../../common/loading-spinner";
import { ErrorFlashbar } from "../../common/ErrorFlashbar";

export interface SelectExistingNodeModalProps {
    visible: boolean;
    linkableNodes: LinkableNode[];
    loading: boolean;
    error?: AppError;
    onDismiss: () => void;
    onSelect: (selectedNode: LinkableNode) => void;
}

export const SelectExistingNodeModal: React.FC<SelectExistingNodeModalProps> = (props) => {
    const [selectedNodeId, setSelectedNodeId] = React.useState<string>("");
    const [searchTerm, setSearchTerm] = React.useState<string>("");

    // Filter nodes based on search term
    const filteredNodes = React.useMemo(() => {
        if (!searchTerm) return props.linkableNodes;

        const lowerSearch = searchTerm.toLowerCase();
        return props.linkableNodes.filter(node =>
            node.overviewText.toLowerCase().includes(lowerSearch) ||
            node.path?.some(p => p.toLowerCase().includes(lowerSearch))
        );
    }, [props.linkableNodes, searchTerm]);

    // Clear selection when filtered out by search
    React.useEffect(() => {
        if (selectedNodeId && !filteredNodes.some(node => node.nodeId === selectedNodeId)) {
            setSelectedNodeId("");
        }
    }, [selectedNodeId, filteredNodes]);

    const handleSelect = () => {
        const selectedNode = props.linkableNodes.find(n => n.nodeId === selectedNodeId);
        if (selectedNode) {
            props.onSelect(selectedNode);
            // Reset state
            setSelectedNodeId("");
            setSearchTerm("");
        }
    };

    const handleDismiss = () => {
        // Reset state
        setSelectedNodeId("");
        setSearchTerm("");
        props.onDismiss();
    };

    return (
        <Modal
            visible={props.visible}
            onDismiss={handleDismiss}
            header="Link Answer to Existing Node"
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleDismiss}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSelect}
                            disabled={!selectedNodeId}
                        >
                            Link
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="l">
                {props.error && <ErrorFlashbar error={props.error} />}

                {props.loading && <LoadingSpinner />}

                {!props.loading && (
                    <>
                        <FormField
                            label="Search Nodes"
                            description="Filter the list of nodes by name or path"
                        >
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.detail.value)}
                                placeholder="Search by node name or path..."
                                type="search"
                            />
                        </FormField>

                        <FormField
                            label="Select Node to Link"
                            description={`${filteredNodes.length} node${filteredNodes.length !== 1 ? "s" : ""} available`}
                        >
                            {filteredNodes.length === 0 ? (
                                <Box textAlign="center" padding="l">
                                    <TextContent>
                                        <p>
                                            {searchTerm
                                                ? "No nodes match your search"
                                                : "No nodes available to link. All nodes would create a cycle or are already descendants."}
                                        </p>
                                    </TextContent>
                                </Box>
                            ) : (
                                <Select
                                    selectedOption={
                                        selectedNodeId
                                            ? {
                                                  value: selectedNodeId,
                                                  label: filteredNodes.find(n => n.nodeId === selectedNodeId)?.overviewText || ""
                                              }
                                            : null
                                    }
                                    onChange={(e) => setSelectedNodeId(e.detail.selectedOption.value || "")}
                                    options={filteredNodes.map(node => ({
                                        value: node.nodeId,
                                        label: node.overviewText,
                                        description: node.path && node.path.length > 0
                                            ? `Path: ${node.path.join(" → ")} | ${node.isLeaf ? "Leaf Node" : "Intermediate Node"}`
                                            : node.isLeaf ? "Leaf Node" : "Intermediate Node"
                                    }))}
                                    placeholder="Select a node to link to..."
                                    selectedAriaLabel="Selected"
                                />
                            )}
                        </FormField>

                        {selectedNodeId && (
                            <Box variant="awsui-key-label">
                                <TextContent>
                                    <p>
                                        <strong>Note:</strong> This answer will link to the selected existing node
                                        rather than creating a new node. Multiple paths can converge to the same node.
                                    </p>
                                </TextContent>
                            </Box>
                        )}
                    </>
                )}
            </SpaceBetween>
        </Modal>
    );
};